import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../../components/sidebar/Sidebar';
import ugcAdService, {
  MotionMode,
  UgcAdProject,
  UgcAdScene,
  UgcAdCharacterFeatures,
  UgcAdCaptionSync,
  UgcAdTimeline,
  TelopPosition,
  TelopSize,
  UpdateUgcAdProjectParams,
  UpdateUgcAdSceneInput,
} from '../../services/api/ugcAdService';
import InfoStep from '../../components/ugc-ad/InfoStep';
import ScriptStep from '../../components/ugc-ad/ScriptStep';
import CharacterStep from '../../components/ugc-ad/CharacterStep';
import StoryboardStep from '../../components/ugc-ad/StoryboardStep';
import MotionStep from '../../components/ugc-ad/MotionStep';
import PreviewStep from '../../components/ugc-ad/PreviewStep';
import EditorStep from '../../components/ugc-ad/EditorStep';
import ExportStep from '../../components/ugc-ad/ExportStep';
import { UgcAdSceneDraft } from '../../components/ugc-ad/types';
import BetaAlert from '../../components/common/BetaAlert';
import { ResponsiveAppShell, TaskyBadge, TaskyAlert, TaskyTopBar, TaskyPageContainer } from '../../components/tasky';
import { useToast } from '../../contexts/ToastContext';
import {
  GENERATION_BUSY_MESSAGE,
  isTooManyJobsError,
  useGenerationCooldown,
} from '../../hooks/useGenerationCooldown';
import {
  Smartphone,
  ArrowLeft,
  Loader2,
  ClipboardList,
  FileText,
  ImageIcon,
  UserRound,
  Wand2,
  Combine,
  CheckCircle2,
  PlayCircle,
} from 'lucide-react';

/**
 * UGC動画広告（UGC Video Ad）エディタ。
 *
 * ①情報登録 → ②構成案 → ③キャラクター → ④ストーリーボード → ⑤モーション → ⑥プレビュー → ⑦書き出し の
 * 6ステップをタブで自由に行き来できる（SongAdEditorPage 踏襲。ウィザード式の強制遷移はしない）。
 * 歌広告との構造差: 楽曲ステップが無く、②構成案（字コンテ＋セリフのテキスト）のフィックスが
 * そのままシーン確定になる（歌詞→シーン分割の2段階を1段階に統合）。
 *
 * 「詳細編集」（'editor'）はタブに出さない特殊ステップ。⑤プレビューの「詳細編集する」ボタンから
 * のみ入り、自身のツールバーの「戻る/書き出す」で ⑤/⑥ に戻る（song-ad spec §14 と同じ）。
 *
 * - サーバー状態（project）はポーリングで更新される「真実」
 * - シーン設定（セリフ・字コンテ・モーション・テロップ位置/サイズ）のローカルドラフトは
 *   保存操作のときだけサーバーへ送り、ポーリングでは上書きしない
 * - 並び替え・追加・削除は構造変更のため、ドラフトに関わらず即PUT scenesする
 */

type StepKey = 'info' | 'script' | 'character' | 'storyboard' | 'motion' | 'preview' | 'editor' | 'export';

const POLLING_INTERVAL_MS = 5000;

const toSceneDrafts = (project: UgcAdProject): UgcAdSceneDraft[] =>
  (project.scenes ?? []).map((scene) => ({
    id: scene.id,
    dialogue: scene.dialogue,
    direction: scene.direction,
    motionMode: scene.motion_mode,
    motionPrompt: scene.motion_prompt ?? '',
    telopPosition: scene.telop_position,
    telopSize: scene.telop_size,
  }));

/**
 * 「動画化」指定なのにクリップが未生成のシーン（UGC-05）。
 *
 * 絵コンテがまだ無いシーンは④の作業待ちなのでここでは数えない。
 * `sceneDrafts` は未保存のモード変更を含むので、保存前の見た目とも一致させるために使う。
 */
export const pendingClipScenes = (
  scenes: UgcAdScene[],
  sceneDrafts: UgcAdSceneDraft[]
): UgcAdScene[] =>
  scenes.filter((scene) => {
    const mode = sceneDrafts.find((d) => d.id === scene.id)?.motionMode ?? scene.motion_mode;

    return mode === 'clip' && !!scene.storyboard.image && !scene.clip.video;
  });

const hasActiveWork = (project: UgcAdProject): boolean =>
  project.status === 'script_generating' ||
  project.status === 'composing' ||
  project.cast_candidates_status === 'pending' ||
  project.cast_candidates_status === 'processing' ||
  // ③キャラクターの生成中もポーリングする。ここが漏れていると、裏で生成が完了しても
  // 画面が pending のまま止まり「このキャラクターで進む」が押せない
  project.character?.status === 'pending' ||
  project.character?.status === 'processing' ||
  (project.scenes ?? []).some(
    (s) =>
      s.storyboard.status === 'pending' ||
      s.storyboard.status === 'processing' ||
      s.clip.status === 'pending' ||
      s.clip.status === 'processing'
  );

const UgcAdEditorPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeStep, setActiveStep] = useState<StepKey>('info');
  const [project, setProject] = useState<UgcAdProject | null>(null);
  const [sceneDrafts, setSceneDrafts] = useState<UgcAdSceneDraft[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isGeneratingScript, setIsGeneratingScript] = useState(false);
  const [isCastCandidatesRequesting, setIsCastCandidatesRequesting] = useState(false);
  const [isSavingScript, setIsSavingScript] = useState(false);
  const [isSavingSceneDrafts, setIsSavingSceneDrafts] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [isMotionAutoRequesting, setIsMotionAutoRequesting] = useState(false);
  const [isComposeRequesting, setIsComposeRequesting] = useState(false);
  const [generatingSceneIds, setGeneratingSceneIds] = useState<Set<string>>(new Set());
  // ストーリーボード生成の連打対策。state は再描画まで古いので、再入の判定は ref で行う。
  const storyboardInFlightRef = useRef<Set<string>>(new Set());
  const bulkStoryboardRunningRef = useRef(false);
  const [isGeneratingAllStoryboards, setIsGeneratingAllStoryboards] = useState(false);
  const {
    coolingDown: storyboardCoolingDown,
    start: startStoryboardCooldown,
    isActive: isStoryboardCoolingDown,
  } = useGenerationCooldown();
  /** ⑤で「生成して次へ」を押し、クリップの完成を待っている状態（完了したら自動で⑥へ） */
  const [awaitingClipsForPreview, setAwaitingClipsForPreview] = useState(false);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const applyData = useCallback((next: UgcAdProject, resetDrafts: boolean) => {
    setProject(next);
    if (resetDrafts) {
      setSceneDrafts(toSceneDrafts(next));
    }
  }, []);

  const fetchProject = useCallback(
    async (resetDrafts: boolean) => {
      if (!projectId) return;
      try {
        const next = await ugcAdService.getProject(projectId);
        applyData(next, resetDrafts);
        setError(null);
      } catch (e) {
        setError(ugcAdService.extractErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    },
    [projectId, applyData]
  );

  useEffect(() => {
    fetchProject(true);
  }, [fetchProject]);

  // アクティブなジョブがある間だけポーリング（ドラフトは触らない）
  useEffect(() => {
    if (!project || !hasActiveWork(project)) {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    if (pollingRef.current) return;

    pollingRef.current = setInterval(() => fetchProject(false), POLLING_INTERVAL_MS);
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [project, fetchProject]);

  /** 402 credit_insufficient はトースト、それ以外はページ内エラー表示にする */
  const handleActionError = useCallback(
    (e: unknown) => {
      if (ugcAdService.isCreditInsufficientError(e)) {
        showToast('クレジットが不足しています。プランをご確認ください。', 'error');
      } else {
        setError(ugcAdService.extractErrorMessage(e));
      }
    },
    [showToast]
  );

  /** ①情報登録タブの保存。保存成功後は②構成案へ自動遷移する */
  const handleSaveInfo = useCallback(
    async (params: UpdateUgcAdProjectParams): Promise<boolean> => {
      if (!projectId) return false;
      setError(null);
      try {
        const next = await ugcAdService.updateProject(projectId, params);
        applyData(next, true);
        showToast('情報を保存しました');
        setActiveStep('script');
        return true;
      } catch (e) {
        handleActionError(e);
        return false;
      }
    },
    [projectId, applyData, showToast, handleActionError]
  );

  /**
   * ①情報登録の「性別・年代から候補を生成」。非同期jobを発行し、完了は
   * ポーリング（cast_candidates_status → cast_candidates）で自動反映される。
   *
   * レビューP0対応: 性別・年代は①のローカルドラフトで「保存して次へ」までPATCHされないため、
   * そのまま生成すると未保存の選択が候補に反映されない（auto/autoのまま生成される）。
   * 生成の直前に現在のフォーム値だけを先にPATCHしてから発行する。
   */
  const handleGenerateCastCandidates = useCallback(
    async (castGender: UgcAdProject['cast_gender'], castAge: UgcAdProject['cast_age']) => {
      if (!projectId) return;
      setIsCastCandidatesRequesting(true);
      setError(null);
      try {
        await ugcAdService.updateProject(projectId, { cast_gender: castGender, cast_age: castAge });
        const next = await ugcAdService.generateCastCandidates(projectId);
        applyData(next, false);
        showToast('出演者候補の生成を開始しました（クレジットを消費します）');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsCastCandidatesRequesting(false);
      }
    },
    [projectId, applyData, showToast, handleActionError]
  );

  const draftsToScenePayload = useCallback(
    (drafts: UgcAdSceneDraft[]): UpdateUgcAdSceneInput[] =>
      drafts.map((draft) => ({
        id: draft.id,
        dialogue: draft.dialogue.trim(),
        direction: draft.direction.trim(),
        motion_mode: draft.motionMode,
        motion_prompt: draft.motionPrompt.trim() || null,
        telop_position: draft.telopPosition,
        telop_size: draft.telopSize,
      })),
    []
  );

  /**
   * 構成案の生成/AI作り直し。
   * レビューP1対応: シーンが既にある場合は、未保存のローカルドラフト（手直し中のセリフ・字コンテ）を
   * 先にPUTしてから作り直す。手編集が黙って消えるのを防ぎ、AIも手直し後のテキストを踏まえられる。
   */
  const handleGenerateScript = useCallback(
    async (instruction?: string) => {
      if (!projectId) return;
      setIsGeneratingScript(true);
      setError(null);
      try {
        if ((project?.scenes ?? []).length > 0) {
          await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
        }
        const next = await ugcAdService.generateScript(projectId, instruction);
        applyData(next, true);
        showToast(instruction ? 'AIで構成案を修正しました' : '構成案を生成しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsGeneratingScript(false);
      }
    },
    [projectId, project, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]
  );

  /**
   * ②構成案の「構成を確定して次へ」。シーンドラフトをまとめてPUT→構成案フィックス→
   * テロップ生成→③ストーリーボードへ自動遷移。
   *
   * レビューP0対応: テロップが既にある状態での再確定は force=1 で必ず作り直す。
   * 従来は「未生成時のみ初期化」だったため、②でセリフを直して再確定しても
   * 古いセリフのテロップがプレビュー・書き出しに残っていた（「テキストが正」の原則が
   * 2回目以降に破綻）。作り直しは②の確定確認ダイアログで事前に予告している。
   * 初期化失敗はフィックス自体を妨げない（従来どおりブロックしない）。
   */
  const handleSaveScriptAndFix = useCallback(async () => {
    if (!projectId) return;
    setIsSavingScript(true);
    setError(null);
    try {
      await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
      let next = await ugcAdService.fixScript(projectId);
      try {
        next = await ugcAdService.initCaptions(projectId, !!next.timeline?.captions?.length);
      } catch {
        // テロップ初期化に失敗してもフィックス自体は成功しているのでブロックしない
      }
      applyData(next, true);
      showToast('構成案を確定しました');
      // 絵コンテの前にキャラクターを決める（人物をテキストで固定してから描く）
      setActiveStep('character');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsSavingScript(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /**
   * ③キャラクターの生成・再生成。
   *
   * 完了は非同期なので、詳細取得のポーリング（character.status）で拾う。
   */
  const handleGenerateCharacter = useCallback(
    async (features?: UgcAdCharacterFeatures) => {
      if (!projectId) return;
      setError(null);
      try {
        const next = await ugcAdService.generateCharacter(projectId, features);
        applyData(next, false);
        showToast('キャラクターの生成を開始しました');
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, applyData, showToast, handleActionError]
  );

  const markGenerating = useCallback((sceneId: string, on: boolean) => {
    setGeneratingSceneIds((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(sceneId);
      } else {
        next.delete(sceneId);
      }
      return next;
    });
  }, []);

  const handleGenerateStoryboard = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      if (storyboardInFlightRef.current.has(sceneId)) return false;
      if (isStoryboardCoolingDown()) {
        setError(GENERATION_BUSY_MESSAGE);
        return false;
      }
      storyboardInFlightRef.current.add(sceneId);
      markGenerating(sceneId, true);
      setError(null);
      try {
        await ugcAdService.generateStoryboard(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('ストーリーボードの生成を開始しました');
        }
        return true;
      } catch (e) {
        if (isTooManyJobsError(e)) {
          // 同時実行枠が空くまで同じ 429 が返るので、しばらく押せなくする
          startStoryboardCooldown();
          setError(`${GENERATION_BUSY_MESSAGE}（${ugcAdService.extractErrorMessage(e)}）`);
        } else {
          handleActionError(e);
        }
        return false;
      } finally {
        storyboardInFlightRef.current.delete(sceneId);
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast, handleActionError, isStoryboardCoolingDown, startStoryboardCooldown]
  );

  /**
   * クリップ生成。レビューP1対応: モーション指示（motion_prompt）は④のローカルドラフトのため、
   * 保存前に生成すると古い/空の指示で高価な動画生成が走る。生成の直前に必ずドラフトをPUTする
   * （一括生成側は1回だけPUTしてから skipDraftSave で個別生成を呼ぶ）。
   */
  const handleGenerateClip = useCallback(
    async (sceneId: string, options?: { suppressToast?: boolean; skipDraftSave?: boolean }): Promise<boolean> => {
      if (!projectId) return false;
      markGenerating(sceneId, true);
      setError(null);
      try {
        if (!options?.skipDraftSave) {
          const saved = await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
          applyData(saved, true);
        }
        await ugcAdService.generateClip(projectId, sceneId);
        await fetchProject(false);
        if (!options?.suppressToast) {
          showToast('動画クリップの生成を開始しました');
        }
        return true;
      } catch (e) {
        handleActionError(e);
        return false;
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, sceneDrafts, draftsToScenePayload, applyData, fetchProject, markGenerating, showToast, handleActionError]
  );

  /**
   * ストーリーボードの一括生成。
   * レビューP1対応: 自動実行（onlyNew）は未生成（none）のみを対象にし、failed を含めない。
   * failed を自動対象にすると、恒常的に失敗するシーンがある場合に③を開くたび
   * 自動再試行でクレジットが消費され続ける。失敗分の再試行は明示的なボタン操作に限定する。
   */
  const handleGenerateAllStoryboards = useCallback(
    async (options?: { auto?: boolean; onlyNew?: boolean }) => {
      if (!project || bulkStoryboardRunningRef.current) return;
      const targets = (project.scenes ?? []).filter((s) =>
        options?.onlyNew
          ? s.storyboard.status === 'none'
          : s.storyboard.status === 'none' || s.storyboard.status === 'failed'
      );
      if (targets.length === 0) return;
      bulkStoryboardRunningRef.current = true;
      setIsGeneratingAllStoryboards(true);
      let toastShown = false;
      try {
        for (const scene of targets) {
          // eslint-disable-next-line no-await-in-loop
          const succeeded = await handleGenerateStoryboard(scene.id, { suppressToast: true });
          // 429 の後は残りのシーンも同じく 429 になるので打ち切る
          if (!succeeded && isStoryboardCoolingDown()) break;
          if (succeeded && !toastShown) {
            // 自動実行の場合は「自動で」明記＋件数を出す（クレジットを消費する処理のため。song-ad v8踏襲）
            showToast(
              options?.auto
                ? `構成案の確定に続けてストーリーボードの生成を自動で開始しました（${targets.length}件・クレジットを消費します）`
                : 'ストーリーボードの生成を開始しました'
            );
            toastShown = true;
          }
        }
      } finally {
        bulkStoryboardRunningRef.current = false;
        setIsGeneratingAllStoryboards(false);
      }
    },
    [project, handleGenerateStoryboard, showToast, isStoryboardCoolingDown]
  );

  const handleGenerateAllClips = useCallback(
    async (options?: { skipDraftSave?: boolean; suppressToast?: boolean }) => {
      if (!project || !projectId) return;
      const targets = (project.scenes ?? []).filter((s) => {
        const draft = sceneDrafts.find((d) => d.id === s.id);
        const mode = draft?.motionMode ?? s.motion_mode;
        return mode === 'clip' && s.storyboard.image && (s.clip.status === 'none' || s.clip.status === 'failed');
      });
      if (targets.length === 0) return;
      // モーション指示などのドラフトを先に1回だけ保存してから、各シーンの生成を発行する
      if (!options?.skipDraftSave) {
        try {
          const saved = await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
          applyData(saved, true);
        } catch (e) {
          handleActionError(e);
          return;
        }
      }
      let toastShown = false;
      for (const scene of targets) {
        // eslint-disable-next-line no-await-in-loop
        const succeeded = await handleGenerateClip(scene.id, { suppressToast: true, skipDraftSave: true });
        if (succeeded && !toastShown && !options?.suppressToast) {
          showToast('動画クリップの生成を開始しました');
          toastShown = true;
        }
      }
    },
    [project, projectId, sceneDrafts, draftsToScenePayload, applyData, handleGenerateClip, showToast, handleActionError]
  );

  const handleSceneDialogueChange = useCallback((sceneId: string, text: string) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, dialogue: text } : d)));
  }, []);

  const handleSceneDirectionChange = useCallback((sceneId: string, text: string) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, direction: text } : d)));
  }, []);

  const handleSetMotionMode = useCallback((sceneId: string, motionMode: MotionMode) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, motionMode } : d)));
  }, []);

  const handleSetMotionModeAll = useCallback((motionMode: MotionMode) => {
    setSceneDrafts((prev) => prev.map((d) => ({ ...d, motionMode })));
  }, []);

  const handleMotionPromptChange = useCallback((sceneId: string, motionPrompt: string) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, motionPrompt } : d)));
  }, []);

  const handleTelopPositionChange = useCallback((sceneId: string, telopPosition: TelopPosition) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, telopPosition } : d)));
  }, []);

  const handleTelopSizeChange = useCallback((sceneId: string, telopSize: TelopSize) => {
    setSceneDrafts((prev) => prev.map((d) => (d.id === sceneId ? { ...d, telopSize } : d)));
  }, []);

  const handleSaveSceneDrafts = useCallback(async () => {
    if (!projectId) return;
    setIsSavingSceneDrafts(true);
    setError(null);
    try {
      const next = await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
      applyData(next, true);
      showToast('設定を保存しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsSavingSceneDrafts(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /**
   * ⑤モーションの主要ボタン（UGC-05）。
   *
   * 未生成のクリップがあるなら、まず全件の生成を投げて**その場で待つ**。
   * 以前は保存だけしてプレビューへ進めてしまい、遷移してから
   * 「3件のクリップが未生成」と知らされて前の画面へ戻る必要があった。
   */
  const handleSaveMotionAndProceed = useCallback(async () => {
    if (!projectId || !project) return;
    setIsSavingSceneDrafts(true);
    setError(null);
    try {
      const next = await ugcAdService.updateScenes(projectId, draftsToScenePayload(sceneDrafts));
      applyData(next, true);

      const pending = pendingClipScenes(next.scenes ?? [], toSceneDrafts(next));

      if (pending.length === 0) {
        showToast('設定を保存しました');
        setActiveStep('preview');
        return;
      }

      // 生成完了を待って自動でプレビューへ進む（下の useEffect が拾う）
      setAwaitingClipsForPreview(true);
      await handleGenerateAllClips({ skipDraftSave: true });
      showToast(`${pending.length}件のクリップを生成しています。完了すると自動でプレビューへ進みます`);
    } catch (e) {
      setAwaitingClipsForPreview(false);
      handleActionError(e);
    } finally {
      setIsSavingSceneDrafts(false);
    }
  }, [
    projectId,
    project,
    sceneDrafts,
    draftsToScenePayload,
    applyData,
    handleGenerateAllClips,
    showToast,
    handleActionError,
  ]);

  /** テロップと音声の合わせ方（advanced_settings.caption_sync）を切り替える */
  const handleUpdateCaptionSync = useCallback(
    async (mode: UgcAdCaptionSync) => {
      if (!projectId || !project) return;
      setError(null);
      try {
        const next = await ugcAdService.updateProject(projectId, {
          advanced_settings: {
            required_words: project.advanced_settings?.required_words ?? [],
            banned_words: project.advanced_settings?.banned_words ?? [],
            cta: project.advanced_settings?.cta ?? null,
            logo_image_key: project.advanced_settings?.logo_image_key ?? null,
            caption_sync: mode,
          },
        });
        applyData(next, false);
        showToast(
          mode === 'whisper'
            ? 'テロップを音声に合わせて書き出します（書き出しに時間がかかります）'
            : 'テロップをクリップの長さに合わせて配分します'
        );
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, project, applyData, showToast, handleActionError]
  );

  /** シーンの並び替え。ドラッグ&ドロップで確定した新しいid順を即PUTする（ドラフト保存を待たない） */
  const handleReorderScenes = useCallback(
    async (orderedSceneIds: string[]) => {
      if (!projectId) return;
      const bySceneId = new Map(sceneDrafts.map((d) => [d.id, d]));
      const reorderedDrafts = orderedSceneIds
        .map((id) => bySceneId.get(id))
        .filter((d): d is UgcAdSceneDraft => !!d);
      setSceneDrafts(reorderedDrafts);
      setIsReordering(true);
      setError(null);
      try {
        const next = await ugcAdService.updateScenes(projectId, draftsToScenePayload(reorderedDrafts));
        applyData(next, true);
        showToast('シーンを並び替えました');
      } catch (e) {
        handleActionError(e);
        await fetchProject(false);
      } finally {
        setIsReordering(false);
      }
    },
    [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError, fetchProject]
  );

  /** シーン追加（②構成案から）。id無し要素をPUT scenesに含めると新規シーンとして作成される */
  const handleAddScene = useCallback(async () => {
    if (!projectId) return;
    setIsReordering(true);
    setError(null);
    try {
      const payload: UpdateUgcAdSceneInput[] = [
        ...draftsToScenePayload(sceneDrafts),
        { dialogue: '', direction: '', motion_mode: 'clip', motion_prompt: null, telop_position: 'bottom', telop_size: 'lg' },
      ];
      const next = await ugcAdService.updateScenes(projectId, payload);
      applyData(next, true);
      showToast('シーンを追加しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsReordering(false);
    }
  }, [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]);

  /** シーン削除。送らなかった既存idはPUT scenesで削除される */
  const handleDeleteScene = useCallback(
    async (sceneId: string) => {
      if (!projectId) return;
      const remaining = sceneDrafts.filter((d) => d.id !== sceneId);
      setIsReordering(true);
      setError(null);
      try {
        const next = await ugcAdService.updateScenes(projectId, draftsToScenePayload(remaining));
        applyData(next, true);
        showToast('シーンを削除しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsReordering(false);
      }
    },
    [projectId, sceneDrafts, draftsToScenePayload, applyData, showToast, handleActionError]
  );

  /** シーン単位AI修正（202・非同期job）。完了はポーリングで反映される */
  const handleAiEditScene = useCallback(
    async (sceneId: string, instruction: string) => {
      if (!projectId) return;
      markGenerating(sceneId, true);
      setError(null);
      try {
        await ugcAdService.aiEditScene(projectId, sceneId, instruction);
        await fetchProject(false);
        showToast('AI修正を開始しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, fetchProject, markGenerating, showToast, handleActionError]
  );

  const handleReplaceSceneImage = useCallback(
    async (sceneId: string, s3Key: string) => {
      if (!projectId) return;
      markGenerating(sceneId, true);
      setError(null);
      try {
        const next = await ugcAdService.replaceSceneImage(projectId, sceneId, s3Key);
        applyData(next, true);
        showToast('画像を差し替えました');
      } catch (e) {
        handleActionError(e);
      } finally {
        markGenerating(sceneId, false);
      }
    },
    [projectId, applyData, markGenerating, showToast, handleActionError]
  );

  const handleMotionAuto = useCallback(async () => {
    if (!projectId) return;
    setIsMotionAutoRequesting(true);
    setError(null);
    try {
      const next = await ugcAdService.motionAuto(projectId);
      applyData(next, true);
      showToast('トーク・商品・CTAのシーンを優先して動画化に設定しました');
    } catch (e) {
      handleActionError(e);
    } finally {
      setIsMotionAutoRequesting(false);
    }
  }, [projectId, applyData, showToast, handleActionError]);

  /** 詳細編集タイムラインの自動保存。EditorStep内のdebounceタイマーから呼ばれる */
  const handleSaveTimeline = useCallback(
    async (timeline: UgcAdTimeline) => {
      if (!projectId) return;
      const next = await ugcAdService.updateTimeline(projectId, timeline);
      applyData(next, false);
    },
    [projectId, applyData]
  );

  /** ⑤プレビューのCTAトグル。cta_enabled のみPATCHし、ドラフトは触らない */
  const handleUpdateCtaEnabled = useCallback(
    async (enabled: boolean) => {
      if (!projectId) return;
      setError(null);
      try {
        const next = await ugcAdService.updateProject(projectId, { cta_enabled: enabled });
        applyData(next, false);
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, applyData, handleActionError]
  );

  /** ⑤プレビューのCTA文言。既存の required_words/banned_words/logo_image_key は保ったまま cta のみ差し替える */
  const handleUpdateCtaText = useCallback(
    async (text: string) => {
      if (!projectId || !project) return;
      setError(null);
      try {
        const next = await ugcAdService.updateProject(projectId, {
          advanced_settings: {
            required_words: project.advanced_settings?.required_words ?? [],
            banned_words: project.advanced_settings?.banned_words ?? [],
            cta: text.trim() || null,
            logo_image_key: project.advanced_settings?.logo_image_key ?? null,
            caption_sync: project.advanced_settings?.caption_sync ?? 'proportional',
          },
        });
        applyData(next, false);
      } catch (e) {
        handleActionError(e);
      }
    },
    [projectId, project, applyData, handleActionError]
  );

  /**
   * 書き出し。`allowStill` は「クリップ無しの静止画版で書き出す」を
   * 利用者が明示的に選んだときだけ true になる（UGC-08）。
   */
  const handleCompose = useCallback(
    async (allowStill = false) => {
      if (!projectId) return;
      setIsComposeRequesting(true);
      setError(null);
      try {
        await ugcAdService.compose(projectId, allowStill);
        await fetchProject(false);
        showToast(allowStill ? '静止画版の書き出しを開始しました' : '書き出しを開始しました');
      } catch (e) {
        handleActionError(e);
      } finally {
        setIsComposeRequesting(false);
      }
    },
    [projectId, fetchProject, showToast, handleActionError]
  );

  const scenes = useMemo(() => project?.scenes ?? [], [project]);

  // ③ストーリーボードに未生成の画像がある状態で表示されたら自動で一括生成を開始する
  // （song-ad v8 踏襲。プロジェクトごとに一度だけ発火し、意図しない連続課金を避ける）。
  // 対象は「未生成（none）」のみ。failed の自動再試行はしない（レビューP1: ページ再訪のたび
  // 失敗シーンにクレジットが消費され続けるのを防ぐ。再試行はカードの明示ボタンから）
  const autoStoryboardGenTriggeredRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeStep !== 'storyboard' || !project) return;
    if (scenes.length === 0) return;
    // キャラクターが決まる前に焼くと、シーンごとに別人の顔になってしまう。
    // ③でキャラクターを確定するまで自動生成は待つ（手動生成は従来どおり可能）
    if (!project.character?.locked) return;
    if (autoStoryboardGenTriggeredRef.current === project.id) return;
    const hasPending = scenes.some((s) => s.storyboard.status === 'none');
    if (!hasPending) return;
    autoStoryboardGenTriggeredRef.current = project.id;
    void handleGenerateAllStoryboards({ auto: true, onlyNew: true });
  }, [activeStep, project, scenes, handleGenerateAllStoryboards]);

  /** 「動画化」なのにクリップが未生成のシーン。0件になるまで⑥⑦へは進ませない（UGC-05） */
  const pendingClips = useMemo(() => pendingClipScenes(scenes, sceneDrafts), [scenes, sceneDrafts]);
  const isGeneratingClips = useMemo(
    () => scenes.some((s) => s.clip.status === 'pending' || s.clip.status === 'processing'),
    [scenes]
  );

  // 「未生成◯件を生成して次へ」の完了待ち。全件揃ったらプレビューへ送る
  useEffect(() => {
    if (!awaitingClipsForPreview) return;
    if (isGeneratingClips || pendingClips.length > 0) return;

    setAwaitingClipsForPreview(false);
    setActiveStep('preview');
    showToast('クリップが揃いました。プレビューを表示します');
  }, [awaitingClipsForPreview, isGeneratingClips, pendingClips.length, showToast]);

  /**
   * ステップタブの遷移可否。
   * 未生成クリップが残っている間は⑥プレビューを選べない（遷移してから必須作業を知らされないため・UGC-05）。
   *
   * **⑦書き出しは塞がない。** ここを塞ぐと、クリップが失敗したときの逃げ道である
   * 「静止画版の仮プレビュー」（UGC-08）が、それを必要とする状況でだけ到達不能になる。
   * ExportStep 側が不足素材の一覧を出し、本書き出しのボタンを止め、静止画版は
   * 明示チェックを経由させるので、進んでから必須作業を知らされることにはならない。
   */
  const isStepLocked = useCallback(
    (key: StepKey): boolean => (key === 'preview' || key === 'editor') && pendingClips.length > 0,
    [pendingClips.length]
  );

  const steps = useMemo(() => {
    const sceneCount = scenes.length;
    const storyboardDone = scenes.filter((s) => s.storyboard.image).length;
    const motionTargets = sceneDrafts.filter((d) => d.motionMode === 'clip').length;
    const clipDone = scenes.filter((s) => {
      const draft = sceneDrafts.find((d) => d.id === s.id);
      return (draft?.motionMode ?? s.motion_mode) === 'clip' && s.clip.video;
    }).length;
    const finalDone = !!project?.final_video_url;
    const canPreview = sceneCount > 0 && storyboardDone === sceneCount;

    return [
      {
        key: 'info' as StepKey,
        label: '① 情報登録',
        icon: ClipboardList,
        badge: null,
        done: !!project?.product_id || !!project?.title,
      },
      {
        key: 'script' as StepKey,
        label: '② 構成案',
        icon: FileText,
        badge: null,
        done: !!project?.script_fixed_at,
      },
      {
        key: 'character' as StepKey,
        label: '③ キャラクター',
        icon: UserRound,
        badge: null,
        done: !!project?.character?.locked,
      },
      {
        key: 'storyboard' as StepKey,
        label: '④ ストーリーボード',
        icon: ImageIcon,
        badge: sceneCount > 0 ? `${storyboardDone}/${sceneCount}` : null,
        done: sceneCount > 0 && storyboardDone === sceneCount,
      },
      {
        key: 'motion' as StepKey,
        label: '⑤ モーション',
        icon: Wand2,
        badge: motionTargets > 0 ? `${clipDone}/${motionTargets}` : null,
        done: motionTargets > 0 ? clipDone === motionTargets : sceneCount > 0 && storyboardDone === sceneCount,
      },
      {
        key: 'preview' as StepKey,
        label: '⑥ プレビュー',
        icon: PlayCircle,
        badge: null,
        done: canPreview,
      },
      {
        key: 'export' as StepKey,
        label: '⑦ 書き出し',
        icon: Combine,
        badge: null,
        done: finalDone,
      },
    ];
  }, [project, scenes, sceneDrafts]);

  if (isLoading) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="UGC動画広告"
      >
        <div className="flex min-h-[calc(100dvh-56px)] items-center justify-center bg-warm-bg text-gray-400 lg:h-screen">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          読み込み中...
        </div>
      </ResponsiveAppShell>
    );
  }

  if (!project) {
    return (
      <ResponsiveAppShell
        sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
        mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
        topbarTitle="UGC動画広告"
      >
        <div className="flex min-h-[calc(100dvh-56px)] flex-col items-center justify-center bg-warm-bg px-4 text-center text-gray-500 lg:h-screen">
          <p className="text-sm mb-3">{error ?? 'プロジェクトが見つかりません'}</p>
          <button
            onClick={() => navigate('/ads/ugc')}
            className="px-4 py-2 text-sm bg-white border border-warm-border rounded-tasky-sm hover:bg-gray-100"
          >
            一覧へ戻る
          </button>
        </div>
      </ResponsiveAppShell>
    );
  }

  return (
    <ResponsiveAppShell
      sidebar={<Sidebar collapsed={sidebarCollapsed} onToggleCollapse={setSidebarCollapsed} />}
      mobileSidebar={<Sidebar collapsed={false} onToggleCollapse={() => undefined} />}
      topbarTitle={project.title || 'UGC動画広告'}
    >
      <div className="flex min-h-[calc(100dvh-56px)] flex-col overflow-hidden bg-warm-bg lg:h-screen lg:min-h-0">
        {/* Header */}
        <TaskyTopBar
          title={project.title}
          icon={<Smartphone className="w-5 h-5" />}
          leading={(
            <button
              onClick={() => navigate('/ads/ugc')}
              className="p-1 h-8 w-8 hover:bg-gray-100 rounded-md shrink-0"
              title="一覧へ戻る"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
          )}
          sidebarCollapsed={sidebarCollapsed}
          onExpandSidebar={() => setSidebarCollapsed(false)}
        />

        {/* ステップタブ */}
        <div data-tutorial="ads-ugc-steps" className="shrink-0 overflow-x-auto border-b border-warm-border bg-white px-4">
          <div className="mx-auto flex w-max max-w-6xl gap-1 lg:w-auto">
            {steps.map((step) => (
              <button
                key={step.key}
                onClick={() => setActiveStep(step.key)}
                disabled={isStepLocked(step.key)}
                title={
                  isStepLocked(step.key)
                    ? `「動画化」に設定したシーンのうち${pendingClips.length}件がクリップ未生成です。⑤モーションで生成してください`
                    : undefined
                }
                className={`flex items-center gap-1.5 px-3 py-2.5 text-sm border-b-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                  activeStep === step.key
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <step.icon className="w-4 h-4" />
                {step.label}
                {step.badge && (
                  <TaskyBadge variant={step.done ? 'success' : 'secondary'} className="text-[11px]">
                    {step.badge}
                  </TaskyBadge>
                )}
                {step.done && !step.badge && <CheckCircle2 className="w-3.5 h-3.5 text-green-500" />}
              </button>
            ))}
          </div>
        </div>

        {/* Main */}
        <div data-tutorial="ads-ugc-content" className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <TaskyPageContainer>
            <BetaAlert featureName="動画生成" />

            {error && (
              <TaskyAlert tone="error" className="mb-4">
                {error}
              </TaskyAlert>
            )}

            {activeStep === 'info' && (
              <InfoStep
                project={project}
                onSave={handleSaveInfo}
                onGenerateCastCandidates={handleGenerateCastCandidates}
                isCastCandidatesRequesting={isCastCandidatesRequesting}
              />
            )}

            {activeStep === 'script' && (
              <ScriptStep
                project={project}
                scenes={scenes}
                sceneDrafts={sceneDrafts}
                isGenerating={isGeneratingScript}
                isSaving={isSavingScript}
                isReordering={isReordering}
                onGenerate={handleGenerateScript}
                onSceneDialogueChange={handleSceneDialogueChange}
                onSceneDirectionChange={handleSceneDirectionChange}
                onSaveAndFix={handleSaveScriptAndFix}
                onAddScene={handleAddScene}
                onDeleteScene={handleDeleteScene}
                onEditSettings={() => setActiveStep('info')}
              />
            )}

            {activeStep === 'character' && (
              <CharacterStep
                character={project.character}
                onGenerate={handleGenerateCharacter}
                onProceed={() => setActiveStep('storyboard')}
              />
            )}

            {activeStep === 'storyboard' && (
              <StoryboardStep
                scenes={scenes}
                sceneDrafts={sceneDrafts}
                generatingSceneIds={generatingSceneIds}
                isSavingDrafts={isSavingSceneDrafts}
                isReordering={isReordering}
                onGenerate={handleGenerateStoryboard}
                onGenerateAll={handleGenerateAllStoryboards}
                isGeneratingAll={isGeneratingAllStoryboards}
                generationCoolingDown={storyboardCoolingDown}
                onSceneDialogueChange={handleSceneDialogueChange}
                onSaveDrafts={handleSaveSceneDrafts}
                onReorder={handleReorderScenes}
                onDeleteScene={handleDeleteScene}
                onAiEditScene={handleAiEditScene}
                onReplaceSceneImage={handleReplaceSceneImage}
                onProceedToMotion={() => setActiveStep('motion')}
              />
            )}

            {activeStep === 'motion' && (
              <MotionStep
                scenes={scenes}
                sceneDrafts={sceneDrafts}
                generatingSceneIds={generatingSceneIds}
                isSavingDrafts={isSavingSceneDrafts}
                isMotionAutoRequesting={isMotionAutoRequesting}
                onSetMotionMode={handleSetMotionMode}
                onSetMotionModeAll={handleSetMotionModeAll}
                onMotionPromptChange={handleMotionPromptChange}
                onTelopPositionChange={handleTelopPositionChange}
                onTelopSizeChange={handleTelopSizeChange}
                onSaveAndProceed={handleSaveMotionAndProceed}
                onMotionAuto={handleMotionAuto}
                onGenerate={handleGenerateClip}
                onGenerateAll={handleGenerateAllClips}
              />
            )}

            {activeStep === 'preview' && (
              <PreviewStep
                project={project}
                scenes={scenes}
                onProceedToExport={() => setActiveStep('export')}
                onProceedToEditor={() => setActiveStep('editor')}
                onUpdateCtaEnabled={handleUpdateCtaEnabled}
                onUpdateCtaText={handleUpdateCtaText}
                onEditSettings={() => setActiveStep('info')}
                onUpdateCaptionSync={handleUpdateCaptionSync}
              />
            )}

            {activeStep === 'editor' && (
              <EditorStep
                project={project}
                scenes={scenes}
                onSaveTimeline={handleSaveTimeline}
                onBackToPreview={() => setActiveStep('preview')}
                onProceedToExport={() => setActiveStep('export')}
              />
            )}

            {activeStep === 'export' && (
              <ExportStep
                project={project}
                scenes={scenes}
                isRequesting={isComposeRequesting}
                onCompose={handleCompose}
                onGoToScript={() => setActiveStep('script')}
                onGoToStoryboard={() => setActiveStep('storyboard')}
                onGoToMotion={() => setActiveStep('motion')}
              />
            )}
          </TaskyPageContainer>
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default UgcAdEditorPage;
